import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels"; // Import the plugin
import useSlabVsBrands from "./useSlabVsBrands";

// Register Chart.js components and the plugin
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
  ChartDataLabels
);

// Function to generate random colors for each brand
const generateColorPalette = (count) => {
  const colors = [];
  for (let i = 0; i < count; i++) {
    const r = Math.floor((Math.random() * 155) + 100);
    const g = Math.floor((Math.random() * 155) + 100);
    const b = Math.floor((Math.random() * 155) + 100);
    colors.push(`rgba(${r}, ${g}, ${b}, 0.6)`);
  }
  return colors;
};

const SlabVsBrands = ({ values }) => {
  const { graphData, pageLoading } = useSlabVsBrands({ values });

  const { slabs, brands, datasets } = useMemo(() => {
    // Remove "Unknown Slab" from slabs
    const slabs = graphData
      .map((item) => item.slab)
      .filter((slab) => slab && slab !== "Unknown Slab");

    // Get the list of brands without "Unknown Brand"
    const brands = Array.from(
      new Set(
        graphData.flatMap((item) =>
          (item.brands || []).map((brand) => brand.brandname).filter((brand) => brand && brand !== "Unknown Brand")
        )
      )
    );

    // Generate colors for each brand
    const colors = generateColorPalette(brands.length);

    // Build the dataset, only including brands with valid totalshoppingamount (non-null, non-undefined, non-zero, non-NaN)
    const datasets = brands.map((brandName, index) => {
      const brandData = slabs.map((slab) => {
        const slabData = graphData.find((item) => item.slab === slab);
        const brand = slabData?.brands?.find(
          (b) => b.brandname === brandName
        );
        const totalShoppingAmount = brand?.totalshoppingamount;

        // Return value if it's valid (not NaN, null, undefined, or 0)
        return totalShoppingAmount && !isNaN(totalShoppingAmount) && totalShoppingAmount !== 0
          ? parseFloat(totalShoppingAmount)
          : null;
      });

      // Filter out slabs where the brand's totalshoppingamount is null, NaN, or 0
      const filteredData = brandData.filter((data) => data !== null);

      // Return the dataset only if there are valid data points (non-null values)
      if (filteredData.length > 0) {
        return {
          label: brandName,
          data: filteredData,
          backgroundColor: colors[index],
          borderColor: colors[index].replace("0.6", "1"),
          borderWidth: 0.7,
          barPercentage: 1, // 10px bar width (adjust for your chart size)
          categoryPercentage: 0.5, // Space between grouped bars
        };
      }
      return null; // Skip the dataset if all values are null
    }).filter(item => item !== null); // Filter out any null datasets

    return { slabs, brands, datasets };
  }, [graphData]);

  const chartData = {
    labels: slabs,
    datasets,
  };

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          boxWidth: 10,
        },
      },
      title: {
        display: true,
        text: "Slabs vs Brands - Total Shopping Amount",
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => {
            return `${tooltipItem.dataset.label}: ${tooltipItem.raw.toLocaleString()}`;
          },
        },
      },
      datalabels: {
        display: true,
        color: "#000", // Text color
        align: "top", // Positioning the labels above the bars
        font: {
          size: 10, // Adjust font size for better readability
        },
        padding: {
          top: 0, // Increase space above the bar to avoid overlap with the label
        },
        formatter: (value, context) => {
          // Get the brand name and take the initials
          const brandName = context.dataset.label;
          const initials = brandName ? brandName?.split(" ")
            .map(word => word[0]?.toUpperCase())
            .join("") : null; // Get initials from the brand name
          return `${initials.toLocaleString()}`; // Display initials and shopping amount
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Slabs",
        },
        ticks: {
          autoSkip: false, // Make sure labels are not skipped
        },
      },
      y: {
        title: {
          display: true,
          text: "Shopping Amount",
        },
        ticks: {
          beginAtZero: true,
        },
      },
    },
  }), []);

  if (pageLoading) return <div>Loading...</div>;
  if (!datasets.length) return <div>No data available to display</div>;

  return (
    <div style={{ height: "500px", width: "100%" }}>
      <Bar data={chartData} options={chartOptions} />
    </div>
  );
};

export default SlabVsBrands;
